import './App.css';

import { lazy, Suspense as ReactSuspense, useEffect, useState } from 'react';
import moment from 'moment-timezone';

import { ConfigProvider, notification } from 'antd5';
import { Switch, Route, Redirect, useLocation, useHistory } from 'react-router-dom';
import GlobalFooter from 'components/GlobalFooter';
import { SideNav, useNavTabs } from 'components/SideNav';
import PageNotFoundRedirect from 'components/PageNotFoundRedirect';
import { Portals } from 'components/Portals';
import { Content, Layout, Spinner } from 'components/ui';
import { uuidRegex } from 'utils/constants';
import { notifyBugsnag } from 'utils/bugsnag';
import { usePermissions } from 'utils/usePermissions';
import { WORK_ORDER_COLLABORATOR } from 'utils/access-control/roles';
import {
  NEW_IMAGE_VIEWER,
  DARK_MODE,
  RELEASE_NOTIFICATION,
  DAMAGES_DB_MAINTENANCE_BANNER,
} from 'utils/release-toggles';
import { Home } from './routes/Home';
import Reset from './routes/Reset';
import OrgRedirect from './components/OrgRedirect';
import { AcknowledgementModal } from 'components/AcknowledgementModal';
import { OrgMismatchModal } from './components/OrgMismatch';
import { Banner, GlobalBannerContext } from './components/GlobalBanner';
import {
  getReleaseNotificationConfig,
  shouldShowReleaseNotification,
} from './components/ReleaseNotification';
import { useCustomerIdMismatch } from 'utils/account/hooks';
import { useAccountContext } from 'utils/account/AccountContext';
import { AtlasGqlAcknowledgementType, useGetAcknowledgementsQuery } from 'types/atlas-graphql';
import { NotificationContextProvider } from 'components/ui';
import { Theme } from 'utils/color/horizonTheme';
import { useTheme } from 'styled-components';

const AlertsRoot = lazy(() => import('./routes/Alerts/AlertsRoot'));
const ReportsRoot = lazy(() => import('./routes/Reports/ReportsRoot'));
const AnalyticsRoot = lazy(() => import('./routes/Analytics/AnalyticsRoot'));
const ApiExplorerRoot = lazy(() => import('./routes/ApiExplorer/ApiExplorerRoot'));
const AssetDetails = lazy(() => import('horizon/components/Assets/AssetDetails'));
const DeviceFirmwareRoot = lazy(() => import('./routes/DeviceFirmware/DeviceFirmwareRoot'));
const FleetRoot = lazy(() => import('./routes/Fleet/FleetRoot'));
const InspectionsRoot = lazy(() => import('./routes/Inspections/InspectionsRoot'));
const PicturesRoot = lazy(() => import('./routes/Pictures/PicturesRoot'));
const SettingsRoot = lazy(() => import('./routes/Settings/SettingsRoot'));
const TransmissionsLineRoot = lazy(
  () => import('./routes/TransmissionLines/TransmissionLinesRoot')
);
const WorkOrdersRoot = lazy(() => import('./routes/WorkOrders/WorkOrdersRoot'));

let previousLocation;

function SideNavWrapper() {
  const tabs = useNavTabs();
  const { pathname } = useLocation();

  if (pathname.startsWith('/reset')) {
    // when resetting, we show a loading screen, and don't want to show
    // page-level navigation
    return null;
  }

  return <SideNav tabs={tabs} />;
}

function Suspense({ children }) {
  return <ReactSuspense fallback={<Spinner />}>{children}</ReactSuspense>;
}

export default function App() {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const useModal = location.state?.useModal ?? false;
  const [globalBannerMessage, setGlobalBannerMessage] = useState();
  const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false);
  const [customerIdMismatch] = useCustomerIdMismatch();
  const { hasReleaseToggle } = useAccountContext();
  const hasDarkMode = hasReleaseToggle(DARK_MODE);
  const hasReleaseNotificationReleaseToggle = hasReleaseToggle(RELEASE_NOTIFICATION);
  const hasMaintenanceBanner = hasReleaseToggle(DAMAGES_DB_MAINTENANCE_BANNER);
  const [notificationApi, notificationContext] = notification.useNotification();

  /** Temporary banner for DB Upgrade */
  useEffect(() => {
    if (hasMaintenanceBanner && !globalBannerMessage) {
      const maintenanceTime = moment
        .tz('2026-03-26 18:00', 'America/New_York')
        .tz(moment.tz.guess());
      setGlobalBannerMessage(
        `Scheduled maintenance ${maintenanceTime.format('MMMM Do, h:mm a z')}. Brief downtime may be experienced.`
      );
    }
  }, [hasMaintenanceBanner, globalBannerMessage]);

  useEffect(() => {
    if (!hasDarkMode && theme.themeName === Theme.Dark) {
      theme.onChange(Theme.Light);
    }
  }, [theme, hasDarkMode]);

  const { data, error: acknowledgementsError } = useGetAcknowledgementsQuery({
    variables: {
      acknowledgementType: AtlasGqlAcknowledgementType.Gdpr,
    },
  });

  useEffect(() => {
    if (data?.acknowledgements.length > 0) {
      setShowAcknowledgeModal(true);
    }
  }, [data]);

  useEffect(() => {
    if (acknowledgementsError) {
      notifyBugsnag(`GetAcknowledgements error: ${acknowledgementsError.message}`);
    }
  }, [acknowledgementsError]);

  const acknowledgementsByUser = data?.acknowledgements ?? {};

  const { hasRole } = usePermissions();
  const isWoCollaborator = hasRole(WORK_ORDER_COLLABORATOR);

  useEffect(() => {
    if (history.action === 'REPLACE' || useModal) return;

    previousLocation = location;
  }, [location, history, useModal]);

  // should only show once when app is loaded (opened in a new tab, refreshed, etc.)
  useEffect(() => {
    if (hasReleaseNotificationReleaseToggle && shouldShowReleaseNotification()) {
      notificationApi.open(getReleaseNotificationConfig());
    }
  }, [notificationApi]);

  const fullRoutes = (
    <Switch location={useModal && previousLocation ? previousLocation : location}>
      <Route
        exact={true}
        path="/"
        render={props => {
          // handle old "hash" routes with a simple redirect
          const { hash } = props.location;
          return hash ? <Redirect to={hash.replace('#', '')} /> : <Home {...props} />;
        }}
      />

      <Route
        path="/analytics"
        render={() => (
          <Suspense>
            <AnalyticsRoot />
          </Suspense>
        )}
      />

      <Route
        path="/alerts"
        render={() => (
          <Suspense>
            <AlertsRoot />
          </Suspense>
        )}
      />
      <Route
        path={['/transmission-lines', '/transmission-line-uploads']}
        render={() => (
          <Suspense>
            <TransmissionsLineRoot />
          </Suspense>
        )}
      />

      <Route
        path="/settings"
        render={() => (
          <Suspense>
            <SettingsRoot />
          </Suspense>
        )}
      />

      <Route
        path="/device-firmware-management"
        render={() => (
          <Suspense>
            <DeviceFirmwareRoot />
          </Suspense>
        )}
      />

      <Route
        path="/fleet"
        render={() => (
          <Suspense>
            <FleetRoot />
          </Suspense>
        )}
      />

      <Route
        path={['/damages', '/damages2', '/inspections']}
        render={() => (
          <Suspense>
            <InspectionsRoot />
          </Suspense>
        )}
      />

      <Route
        path={['/reports', '/report-deliveries']}
        render={() => (
          <Suspense>
            <ReportsRoot />
          </Suspense>
        )}
      />

      <Route
        path={[`/sites/:id${uuidRegex}`, `/turbines/:id${uuidRegex}`, `/blades/:id${uuidRegex}`]}
        render={({ location }) => {
          const assetsRoute = `/${['assets', ...location.pathname.split('/').slice(2)].join('/')}`;

          return <Redirect to={assetsRoute} />;
        }}
      />

      <Route
        path={[`/assets/:id${uuidRegex}`]}
        render={() => (
          <Suspense>
            <AssetDetails />
          </Suspense>
        )}
      />

      <Route
        path={[
          '/work-order-create',
          '/work-orders',
          '/campaigns',
          '/unplanned-tasks',
          '/tasks',
          '/recurring-task',
        ]}
        render={() => (
          <Suspense>
            <WorkOrdersRoot />
          </Suspense>
        )}
      />

      <Route
        path={['/pictures', '/picture-review']}
        render={() => (
          <Suspense>
            <PicturesRoot />
          </Suspense>
        )}
      />

      <Route exact={true} path="/reset" component={Reset} />

      <Route
        path="/api/:apiName"
        render={() => (
          <Suspense>
            <ApiExplorerRoot />
          </Suspense>
        )}
      />

      <Route path="/o/:orgId" render={() => <OrgRedirect />} />
      <Route component={PageNotFoundRedirect} />
    </Switch>
  );

  const woCollaboratorRoutes = (
    <Switch location={useModal && previousLocation ? previousLocation : location}>
      <Route
        exact={true}
        path="/"
        render={props => {
          // handle old "hash" routes with a simple redirect
          const { hash } = props.location;
          return hash ? <Redirect to={hash.replace('#', '')} /> : <Redirect to={'/work-orders'} />;
        }}
      />

      <Route
        path="/settings"
        render={() => (
          <Suspense>
            <SettingsRoot />
          </Suspense>
        )}
      />

      <Route
        path={['/work-orders', '/campaigns', '/unplanned-tasks', '/tasks']}
        render={() => (
          <Suspense>
            <WorkOrdersRoot />
          </Suspense>
        )}
      />

      <Route
        path={['/reports', '/report-deliveries']}
        render={() => (
          <Suspense>
            <ReportsRoot />
          </Suspense>
        )}
      />

      <Route
        path={[`/inspections/:id${uuidRegex}`]}
        render={() => (
          <Suspense>
            <InspectionsRoot />
          </Suspense>
        )}
      />

      <Route
        path={['/pictures']}
        render={() => (
          <Suspense>
            <PicturesRoot />
          </Suspense>
        )}
      />

      <Route exact={true} path="/reset" component={Reset} />

      <Route
        path="/api/:apiName"
        render={() => (
          <Suspense>
            <ApiExplorerRoot />
          </Suspense>
        )}
      />

      <Route path="/o/:orgId" render={() => <OrgRedirect />} />
      <Route component={PageNotFoundRedirect} />
    </Switch>
  );

  return (
    <ConfigProvider
      /**
       * SkySpecs teal color (for use as antd's primary color)
       * is set in vite.config.mts and here to cover buttons, loaders,
       * etc. (some are affected by the vite config and some by this
       * provider, because antd + vite have some oddities in combination).
       *
       * All other theme config is through styled-components
       * <ThemeProvider /> in ./index.tsx. See `src/utils/color/THEME.md`
       * for detail.
       */
      theme={{
        token: {
          colorPrimary: theme.teal,
          fontFamily: 'Lato',
        },
      }}
    >
      <NotificationContextProvider>
        <GlobalBannerContext.Provider
          value={{ message: globalBannerMessage, setMessage: setGlobalBannerMessage }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100dvh',
              maxHeight: '100dvh',
            }}
          >
            {globalBannerMessage && <Banner message={globalBannerMessage} />}
            <Layout style={{ flexGrow: 1 }}>
              <AcknowledgementModal
                showModal={showAcknowledgeModal}
                setShowModal={setShowAcknowledgeModal}
                acknowledgements={acknowledgementsByUser}
              />
              {customerIdMismatch ? <OrgMismatchModal /> : null}
              {notificationContext}
              <SideNavWrapper />
              {/* This classname is the same as the constant APP_SCROLL_CONTAINER_CLASSNAME but has to be a string literal here for the use-case to work properly */}
              <Layout className="scrollable-app-content-wrapper" style={{ overflowY: 'scroll' }}>
                <Content>
                  {isWoCollaborator ? woCollaboratorRoutes : fullRoutes}

                  {/* Not sure why there's a separate Switch... */}
                  {useModal && previousLocation ? (
                    <Switch>
                      <Route
                        path={
                          hasReleaseToggle(NEW_IMAGE_VIEWER)
                            ? ['/pictures', '/picture-review']
                            : '/pictures/:ids'
                        }
                        render={() => (
                          <Suspense>
                            <PicturesRoot />
                          </Suspense>
                        )}
                      />

                      <Route
                        path="/transmission-lines"
                        render={() => (
                          <Suspense>
                            <TransmissionsLineRoot />
                          </Suspense>
                        )}
                      />
                    </Switch>
                  ) : null}
                  <GlobalFooter />
                </Content>

                <Portals />
              </Layout>
            </Layout>
          </div>
        </GlobalBannerContext.Provider>
      </NotificationContextProvider>
    </ConfigProvider>
  );
}
